import React, { useContext, useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Transition } from 'react-transition-group';
import Modal from '../../components/common/Modal/Modal';
import Input from '../../components/common/Input/Input';
import { Context } from '../../main';
import { useNotification } from '../../hooks/useNotification';
import { useCountdown } from '../../hooks/useCountdown';

const RESEND_COOLDOWN_SEC = 60; // синхронно з TWO_FA_RESEND_COOLDOWN_SEC бекенду

// Вікно вводу email-коду 2FA. Рендериться з Login.jsx, коли
// store.twoFactorRequired (логін+пароль пройдено, сесії ще немає).
const TwoFactorModalComponent = () => {
    const { store } = useContext(Context);
    const notification = useNotification();
    const nodeRef = useRef(null);
    const inputRef = useRef(null);
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const { seconds, isRunning, restart } = useCountdown(RESEND_COOLDOWN_SEC);

    const isOpen = store.twoFactorRequired;
    // Бекенд віддає вже замасковані адреси (buh***@gromada.gov.ua). Їх може бути
    // кілька — код летить і на additional_emails.
    const sentTo = (store.twoFactorSentTo ?? []).join(', ');

    // Скидання стану + автофокус при кожному відкритті
    useEffect(() => {
        if (isOpen) {
            setCode('');
            restart(RESEND_COOLDOWN_SEC);
            setTimeout(() => inputRef.current?.focus(), 250);
        }
    }, [isOpen, restart]);

    const onCodeChange = (_name, value) => {
        setCode(value.replace(/\D/g, '').slice(0, 6));
    };

    const handleSubmit = async () => {
        if (code.length !== 6 || submitting) return;
        setSubmitting(true);
        const success = await store.verifyTwoFactor(code);
        setSubmitting(false);
        if (!success) {
            notification({
                type: 'warning',
                title: 'Помилка підтвердження',
                message: store.errorMessage,
                placement: 'top'
            });
            setCode('');
            inputRef.current?.focus();
        }
    };

    const handleResend = async () => {
        if (isRunning || resending) return;
        setResending(true);
        const success = await store.resendTwoFactorCode();
        setResending(false);
        if (success) {
            restart(RESEND_COOLDOWN_SEC);
            notification({
                type: 'success',
                title: 'Успіх',
                message: sentTo
                    ? `Код надіслано повторно на ${sentTo}`
                    : 'Код надіслано повторно на вашу електронну пошту',
                placement: 'top'
            });
        } else {
            notification({
                type: 'warning',
                title: 'Помилка',
                message: store.errorMessage,
                placement: 'top'
            });
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <Transition in={isOpen} timeout={200} unmountOnExit nodeRef={nodeRef}>
            {state => (
                <Modal
                    className={state === 'entered' ? 'modal-window-wrapper--active' : ''}
                    onClose={() => store.cancelTwoFactor()}
                    onOk={handleSubmit}
                    title="Підтвердження входу"
                    okText={submitting ? 'Перевірка...' : 'Підтвердити'}
                    cancelText="Скасувати"
                    confirmLoading={submitting}
                    okDisabled={code.length !== 6}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p className="paragraph">
                            {sentTo
                                ? <>Код підтвердження надіслано на <b>{sentTo}</b>. </>
                                : 'На вашу електронну пошту надіслано 6-значний код підтвердження. '}
                            Введіть його, щоб завершити вхід. Код дійсний 10 хвилин.
                        </p>
                        <Input
                            ref={inputRef}
                            type="text"
                            name="code"
                            value={code}
                            onChange={onCodeChange}
                            onKeyDown={onKeyDown}
                            placeholder="______"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '20px' }}
                        />
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={isRunning || resending}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                alignSelf: 'flex-start',
                                cursor: isRunning || resending ? 'default' : 'pointer',
                                color: isRunning || resending ? '#7b8794' : '#2563eb',
                                textDecoration: isRunning || resending ? 'none' : 'underline',
                                fontSize: '14px'
                            }}
                        >
                            {resending
                                ? 'Відправка...'
                                : isRunning
                                    ? `Надіслати повторно (${seconds}с)`
                                    : 'Надіслати повторно'}
                        </button>
                    </div>
                </Modal>
            )}
        </Transition>
    );
};

const TwoFactorModal = observer(TwoFactorModalComponent);
export default TwoFactorModal;
